import * as React from "react";
import { type DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DateRangePickerProps {
  value?: DateRange | undefined;
  onChange?: (range: DateRange | undefined) => void;
  numberOfMonths?: number;
  placeholder?: string;
}

export function DateRangePicker({
  value,
  onChange,
  numberOfMonths = 1,
  placeholder = "Select date range",
}: DateRangePickerProps) {
  const [local, setLocal] = React.useState<DateRange | undefined>(value);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const label = local?.from
    ? local.to
      ? `${format(local.from, "MMM dd, yyyy")} – ${format(local.to, "MMM dd, yyyy")}`
      : format(local.from, "MMM dd, yyyy")
    : placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[280px] justify-start text-left font-normal text-[var(--secondary-text-color)] text-[13px] px-3",
            !local && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={local}
          defaultMonth={local?.from}
          numberOfMonths={numberOfMonths}
          onSelect={(r) => {
            setLocal(r);
            onChange?.(r);
          }}
          showOutsideDays={false}
        />
      </PopoverContent>
    </Popover>
  );
}
